import { ReactNode } from 'react';
import { NavSidebar } from '../superadmin/SuperadminSidebar';
import { AdminTopBar } from './AdminTopBar';
import './admin-layout.css';

/**
 * Props para SuperAdminLayout
 */
interface SuperAdminLayoutProps {
  /**
   * Contenido a renderizar (generalmente SuperAdminDashboard)
   */
  children: ReactNode;
}

/**
 * SuperAdminLayout - Layout fullscreen para dashboard de administración
 *
 * @version 1.0
 * @module core/layouts
 *
 * @description
 * Layout diseñado específicamente para el dashboard de SuperAdmin.
 * Incluye topbar horizontal con background sólido (SIN glassmorphism).
 * Completamente independiente de MapLayout.
 *
 * @remarks
 * **Características**:
 * - Fullscreen (100vw x 100vh)
 * - AdminTopBar fixed en la parte superior (z-index: var(--z-navbar))
 * - Background sólido (var(--bg-primary)) - SIN glassmorphism
 * - Área de contenido con scroll vertical
 * - NO incluye paneles laterales (LayersPanel, InsightsPanel, BottomToolbar)
 *
 * **Uso**:
 * Exclusivamente para la ruta `/admin/dashboard` (SuperAdmin).
 * Para usuarios normales con mapa, usar MapLayout.
 *
 * **Diferencias con MapLayout**:
 * - SuperAdminLayout: AdminTopBar sólido + contenido con scroll
 * - MapLayout: TopBar con glassmorphism + mapa fullscreen + paneles
 *
 * @example
 * ```tsx
 * import { SuperAdminLayout } from '@/core/layouts';
 * import { SuperAdminDashboard } from '@/core/dashboard';
 *
 * function AdminDashboardPage() {
 *   return (
 *     <SuperAdminLayout>
 *       <SuperAdminDashboard />
 *     </SuperAdminLayout>
 *   );
 * }
 * ```
 */
export function SuperAdminLayout({
  children,
}: SuperAdminLayoutProps): JSX.Element {
  return (
    <div className="admin-layout">
      <AdminTopBar />
      <div className="admin-body">
        <NavSidebar />
        <main className="admin-page">{children}</main>
      </div>
    </div>
  );
}
